import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, RotateCcw, Star, Brain, Share2, Check, Trophy } from "lucide-react";
import { Button } from "../components/ui/button";
import { QUIZ_QUESTIONS, DREAM_TYPES } from "../data/quizQuestions";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DreamQuiz({ challengeData, onChallengeComplete }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.substring(0, 2) || "fi";
  const questions_pool = QUIZ_QUESTIONS[lang] || QUIZ_QUESTIONS.fi;
  const dreamTypes = DREAM_TYPES[lang] || DREAM_TYPES.fi;
  const [gameState, setGameState] = useState(challengeData ? "challenge" : "menu");
  const [localChallenge] = useState(challengeData);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState(null);
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [bestScore, setBestScore] = useState(() => {
    try { return parseInt(localStorage.getItem("uneksia_quiz_best") || "0"); }
    catch { return 0; }
  });

  // Fetch leaderboard
  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/games/quiz-leaderboard`);
      setLeaderboard(res.data.leaderboard || []);
    } catch {}
  }, []);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  // Save score to backend
  const saveScore = useCallback(async (finalScore, type) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      await axios.post(`${API}/api/games/quiz-score`, {
        score: finalScore,
        dream_type: type,
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchLeaderboard();
    } catch {}
  }, [fetchLeaderboard]);

  const startQuiz = useCallback(() => {
    const shuffled = [...questions_pool].sort(() => Math.random() - 0.5).slice(0, 10);
    setQuestions(shuffled);
    setCurrentQ(0);
    setSelected(null);
    scoreRef.current = 0;
    setScore(0);
    setAnswered(false);
    setTransitioning(false);
    setGameState("playing");
  }, [questions_pool]);

  const handleAnswer = useCallback((idx) => {
    if (answered || transitioning) return;
    setSelected(idx);
    setAnswered(true);
    if (idx === questions[currentQ].correct) {
      scoreRef.current += 1;
      setScore(scoreRef.current);
    }
  }, [answered, transitioning, questions, currentQ]);

  const nextQuestion = useCallback(() => {
    if (currentQ + 1 >= questions.length) {
      const finalScore = scoreRef.current;
      if (finalScore > bestScore) {
        setBestScore(finalScore);
        localStorage.setItem("uneksia_quiz_best", String(finalScore));
      }
      // Find dream type for this score
      const allTypes = DREAM_TYPES[lang] || DREAM_TYPES.fi;
      const dt = allTypes.find(d => finalScore >= d.min && finalScore <= d.max) || allTypes[0];
      saveScore(finalScore, dt.type);
      if (localChallenge && onChallengeComplete) {
        onChallengeComplete(finalScore);
      }
      setGameState("result");
    } else {
      setTransitioning(true);
      setTimeout(() => {
        setCurrentQ(c => c + 1);
        setSelected(null);
        setAnswered(false);
        setTransitioning(false);
      }, 200);
    }
  }, [currentQ, questions, bestScore, localChallenge, onChallengeComplete, saveScore, lang]);

  const dreamType = useMemo(() => {
    return dreamTypes.find(dt => score >= dt.min && score <= dt.max) || dreamTypes[0];
  }, [score, dreamTypes]);

  const shareChallenge = useCallback(async () => {
    const userStr = localStorage.getItem("user");
    const userName = userStr ? (JSON.parse(userStr).name || "?") : "?";
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      challenge: "true",
      score: String(score),
      name: userName,
      type: dreamType.type,
    });
    const shareUrl = `${baseUrl}/games?${params.toString()}`;
    const shareTitle = t("games.shareTitle", { score });
    const shareText = t("games.shareText", { score, type: dreamType.type });

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        return;
      } catch {}
    }

    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  }, [score, dreamType, t]);

  // Leaderboard component
  const LeaderboardTable = () => {
    if (leaderboard.length === 0) return null;
    return (
      <div data-testid="quiz-leaderboard" className="mt-8 max-w-md mx-auto">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Trophy className="w-4 h-4 text-amber-500" />
          <h4 className="font-heading text-void text-sm font-medium">
            {t("games.leaderboard") || "Tulostaulu"}
          </h4>
        </div>
        <div className="rounded-xl border border-purple-100/60 overflow-hidden bg-white/50 backdrop-blur-sm">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="bg-purple-50/60 text-void/60 text-xs">
                <th className="py-2 px-3 text-left w-8">#</th>
                <th className="py-2 px-3 text-left">{t("games.player") || "Pelaaja"}</th>
                <th className="py-2 px-3 text-center">{t("games.score") || "Pisteet"}</th>
                <th className="py-2 px-3 text-right">{t("games.type") || "Tyyppi"}</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, i) => (
                <tr
                  key={i}
                  className={`border-t border-purple-50/60 ${i < 3 ? "bg-amber-50/30" : ""}`}
                >
                  <td className="py-2.5 px-3 text-void/50">
                    {i === 0 ? "\u{1F947}" : i === 1 ? "\u{1F948}" : i === 2 ? "\u{1F949}" : i + 1}
                  </td>
                  <td className="py-2.5 px-3 text-void font-medium truncate max-w-[120px]">
                    {entry.user_name}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className="inline-flex items-center gap-1 text-void font-medium">
                      {entry.best_score}<span className="text-void/40">/10</span>
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-right text-purple-600 text-xs">
                    {entry.dream_type}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Challenge acceptance screen
  if (gameState === "challenge" && localChallenge) {
    return (
      <div data-testid="quiz-challenge" className="text-center space-y-6 py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 mb-2">
          <Trophy className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-heading font-light text-void">
          {t("games.quizTitle")}
        </h3>
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 max-w-md mx-auto border border-purple-200/50">
          <p className="text-void/80 font-body text-base leading-relaxed">
            {t("games.challengeBanner", {
              name: localChallenge.name,
              score: localChallenge.score,
            })}
          </p>
          <div className="flex justify-center gap-1 mt-3">
            {Array.from({ length: 10 }, (_, i) => (
              <Star
                key={i}
                className={`w-5 h-5 ${i < localChallenge.score ? "text-amber-400 fill-amber-400" : "text-gray-200"}`}
              />
            ))}
          </div>
          <p className="text-sm text-purple-600 font-medium mt-2">{localChallenge.type}</p>
        </div>
        <Button
          data-testid="challenge-accept"
          onClick={startQuiz}
          className="rounded-full bg-void text-white hover:bg-void/90 px-8 py-3 text-base"
        >
          <Trophy className="w-4 h-4 mr-2" />
          {t("games.challengeAccept")}
        </Button>
        <LeaderboardTable />
      </div>
    );
  }

  if (gameState === "menu") {
    return (
      <div data-testid="quiz-menu" className="text-center space-y-8">
        <div>
          <h3 className="text-2xl font-heading font-light text-void mb-2">
            {t("games.quizTitle") || "Unitietovisailu"}
          </h3>
          <p className="text-void/60 font-body text-sm max-w-md mx-auto">
            {t("games.quizDesc") || "Testaa tietämyksesi unista, unisymboleista ja unien tieteestä. 10 kysymystä per kierros!"}
          </p>
        </div>

        <button
          data-testid="quiz-start"
          onClick={startQuiz}
          className="group relative mx-auto block p-8 rounded-2xl border border-purple-200/50 bg-white/60 backdrop-blur-sm hover:bg-purple-50/80 hover:border-purple-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
        >
          <div className="text-4xl mb-3">{"\u{1F9E0}"}</div>
          <div className="font-heading text-void font-medium text-lg">{t("games.startQuiz") || "Aloita visailu"}</div>
          <div className="text-xs text-void/50 font-body mt-1">10 {t("games.questions") || "kysymystä"}</div>
          {bestScore > 0 && (
            <div className="mt-3 pt-2 border-t border-purple-100">
              <div className="flex items-center justify-center gap-1 text-xs text-purple-600">
                <Star className="w-3 h-3" />
                <span>{t("games.bestScore") || "Paras"}: {bestScore}/10</span>
              </div>
            </div>
          )}
        </button>

        <LeaderboardTable />
      </div>
    );
  }

  if (gameState === "result") {
    const challengeComparison = localChallenge ? (() => {
      const cs = parseInt(localChallenge.score);
      if (score > cs) return "won";
      if (score < cs) return "lost";
      return "tied";
    })() : null;

    return (
      <div data-testid="quiz-result" className="text-center space-y-6 py-8">
        <div className="text-6xl">{dreamType.emoji}</div>
        <h3 className="text-2xl font-heading font-light text-void">
          {dreamType.type}
        </h3>
        <p className="text-void/60 font-body text-sm max-w-sm mx-auto">{dreamType.desc}</p>

        <div className="flex justify-center gap-1">
          {Array.from({ length: 10 }, (_, i) => (
            <Star
              key={i}
              className={`w-6 h-6 ${i < score ? "text-amber-400 fill-amber-400" : "text-gray-200"}`}
            />
          ))}
        </div>

        <div className="text-3xl font-heading text-void">
          {score}<span className="text-lg text-void/50">/10</span>
        </div>

        {/* Challenge comparison */}
        {localChallenge && challengeComparison && (
          <div data-testid="challenge-comparison" className={`max-w-sm mx-auto rounded-2xl p-5 border ${
            challengeComparison === "won"
              ? "bg-emerald-50 border-emerald-200"
              : challengeComparison === "lost"
              ? "bg-red-50 border-red-200"
              : "bg-amber-50 border-amber-200"
          }`}>
            <p className="font-heading text-lg mb-3">
              {challengeComparison === "won" && t("games.youWon")}
              {challengeComparison === "lost" && t("games.youLost")}
              {challengeComparison === "tied" && t("games.youTied")}
            </p>
            <div className="flex items-center justify-center gap-6 text-sm font-body">
              <div>
                <div className="text-void/50 text-xs mb-1">{t("games.yourResult")}</div>
                <div className="text-xl font-heading">{score}/10</div>
              </div>
              <div className="text-void/30 text-lg">vs</div>
              <div>
                <div className="text-void/50 text-xs mb-1">{localChallenge.name}</div>
                <div className="text-xl font-heading">{localChallenge.score}/10</div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-3">
          <Button
            data-testid="quiz-play-again"
            onClick={startQuiz}
            className="rounded-full bg-void text-white hover:bg-void/90"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t("games.playAgain") || "Pelaa uudestaan"}
          </Button>

          <Button
            data-testid="quiz-challenge-share"
            onClick={shareChallenge}
            variant="outline"
            className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2 text-emerald-500" />
                {t("games.linkCopied")}
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4 mr-2" />
                {t("games.challengeFriend")}
              </>
            )}
          </Button>

          <Button
            data-testid="quiz-back-to-menu"
            variant="outline"
            onClick={() => { setGameState("menu"); }}
            className="rounded-full"
          >
            {t("games.back") || "Takaisin"}
          </Button>
        </div>

        <LeaderboardTable />
      </div>
    );
  }

  const q = questions[currentQ];
  if (!q) return null;

  return (
    <div data-testid="quiz-playing" className="max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-sm font-body text-void/60">
          <Brain className="w-4 h-4" />
          <span>{currentQ + 1} / {questions.length}</span>
        </div>
        <div className="flex items-center gap-1">
          <Star className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-body text-void/70">{score}</span>
        </div>
      </div>

      <div className="w-full bg-purple-100 rounded-full h-1.5 mb-8">
        <div
          className="bg-purple-500 h-1.5 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }}
        />
      </div>

      <div
        className={`transition-opacity duration-200 ${transitioning ? 'opacity-0' : 'opacity-100'}`}
      >
        <h4 className="text-lg font-heading text-void mb-6 leading-relaxed">{q.question}</h4>

        <div className="space-y-3">
          {q.options.map((opt, idx) => {
            let style = "border-purple-100 bg-white/60 hover:border-purple-300 hover:bg-purple-50/50 cursor-pointer";
            if (answered) {
              if (idx === q.correct) {
                style = "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200";
              } else if (idx === selected && idx !== q.correct) {
                style = "border-red-300 bg-red-50 ring-1 ring-red-200";
              } else {
                style = "border-gray-100 bg-gray-50/50 opacity-50";
              }
            }
            return (
              <button
                key={`${currentQ}-${idx}`}
                data-testid={`quiz-option-${idx}`}
                onClick={() => handleAnswer(idx)}
                disabled={answered}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 font-body text-sm text-void ${style}`}
              >
                <span className="inline-flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center text-xs font-medium text-purple-700 flex-shrink-0">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  {opt}
                </span>
              </button>
            );
          })}
        </div>

        {answered && (
          <div className="mt-6 space-y-4 animate-in fade-in duration-300">
            <div className={`p-4 rounded-xl text-sm font-body ${
              selected === q.correct ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"
            }`}>
              {selected === q.correct ? "\u{2705} " : "\u{1F4A1} "}
              {q.explanation}
            </div>
            <div className="text-center">
              <Button
                data-testid="quiz-next"
                onClick={nextQuestion}
                className="rounded-full bg-void text-white hover:bg-void/90"
              >
                {currentQ + 1 >= questions.length
                  ? (t("games.showResults") || "Näytä tulokset")
                  : (t("games.nextQuestion") || "Seuraava kysymys")}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
